import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import type { CutSaveState } from '../doc/useCutAutosave';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { editorMessages } from '../messages/editorMessages';
import { panelMessages } from '../messages/panelMessages';
import { SAVE_STATE_MESSAGES } from '../topbar/saveStateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PhoneTopProps {
  saveState: CutSaveState;
  onBack: () => void;
  onMake: () => void;
}

/** The top bar of the phone (design f1): back, the song and the save state, "Make · 3". */
const PhoneTopComponent: React.FC<PhoneTopProps> = ({ saveState, onBack, onMake }) => {
  const intl = useIntl();
  const { data, cutUuid, readOnly, doc } = useMusicVideoEditor();
  const number = data.cuts.find((cut) => cut.uuid === cutUuid)?.number ?? 1;
  const state = intl.formatMessage(readOnly ? editorMessages.saveReadOnly : SAVE_STATE_MESSAGES[saveState]);
  return (
    <header className={styles.phoneTop}>
      <button
        type="button"
        className={styles.topButton}
        aria-label={intl.formatMessage(editorMessages.back)}
        onClick={onBack}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <div className={styles.column}>
        <b>{data.song.title}</b>
        <small>{intl.formatMessage(panelMessages.phoneSavedLine, { number, state })}</small>
      </div>
      <span className={styles.grow} />
      <SMLButton size="sm" omitCap disabled={readOnly || doc.clips.length === 0} onClick={onMake}>
        {data.plan.canRender
          ? intl.formatMessage(editorMessages.makeShort, { credits: data.plan.renderCredits })
          : intl.formatMessage(editorMessages.makeLocked)}
      </SMLButton>
    </header>
  );
};

export const PhoneTop = TypedMemo(PhoneTopComponent);
